/**
 * Contracts page (shared) — used by Buyer (/dashboard/contracts),
 * Publisher (/publisher/contracts) and Affiliate (/affiliate/contracts).
 *
 * - Lists my contracts (auto-creates the default draft on first load)
 * - Lets the counterparty sign in-portal (typed OR drawn)
 * - PDF preview via embedded iframe (token-protected URL)
 * - On successful sign, the backend renders the PDF + emails it to the signer
 */
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FileText, Lock, CheckCircle2, Loader2, Download, ExternalLink, Pen, Type,
  ShieldCheck, X, AlertTriangle,
} from "lucide-react";
import { api, formatApiError, BACKEND_URL, TOKEN_KEY } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SignaturePad from "@/components/contracts/SignaturePad";

export default function ContractsPage({ role = "buyer", title = "Your contracts" }) {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [signOpen, setSignOpen] = useState(null); // contract object or null
  const [pdfOpen, setPdfOpen]   = useState(null); // {contract, url}

  // iter99: when the UnsignedContractBanner CTA navigates here with ?sign=<id>,
  // auto-open the signing modal for that contract on first load. Without this,
  // a user already on the contracts page clicking "Sign now" sees nothing
  // happen (the navigation is a no-op since they're already on the page).
  const location = useLocation();
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/contracts/my");
      setItems(data.items || []);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // After items load, if the URL has ?sign=<contractId>, open the signing modal
  // for that contract (if it exists and isn't already signed). Then strip the
  // query param so a refresh doesn't reopen it.
  useEffect(() => {
    if (loading || items.length === 0) return;
    const params = new URLSearchParams(location.search);
    const signId = params.get("sign");
    if (!signId) return;
    const target = items.find((c) => c.id === signId);
    if (target && target.status !== "signed") {
      setSignOpen(target);
    }
    // Clean the URL either way — prevents accidental re-opens on refresh.
    navigate(location.pathname, { replace: true });
  }, [loading, items, location.search, location.pathname, navigate]);

  const onPreview = async (c) => {
    try {
      const { data } = await api.get(`/contracts/${c.id}/pdf-link`);
      setPdfOpen({ contract: c, url: data.url });
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const onDownload = async (c) => {
    try {
      const { data } = await api.get(`/contracts/${c.id}/pdf-link`);
      // Append &dl=1 so the server sets Content-Disposition: attachment instead of inline.
      const dlUrl = data.url + (data.url.includes("?") ? "&" : "?") + "dl=1";
      window.open(dlUrl, "_blank", "noopener");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="contracts-page">
      {/* Heading */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl tracking-[-0.02em] font-semibold text-slate-900">{title}</h1>
          <p className="text-[13.5px] text-slate-500 mt-1">
            Master agreements between your organisation and Connexis Group Ltd. Signed copies are emailed to you and remain available here.
          </p>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-50 border border-slate-200 px-3 h-8 text-[11px] font-mono text-slate-600">
          <ShieldCheck className="size-3 text-emerald-500" strokeWidth={2.2} /> Connexis Group Ltd · Co. no. 17214446
        </span>
      </div>

      {/* List */}
      {loading && (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center">
          <Loader2 className="size-5 animate-spin text-slate-400 mx-auto" />
          <p className="mt-2 text-sm text-slate-500">Loading your contracts…</p>
        </div>
      )}

      {!loading && error && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3" data-testid="contracts-error">
          <AlertTriangle className="size-4 text-amber-600 mt-0.5" />
          <div>
            <div className="text-sm font-medium text-amber-900">Could not load contracts</div>
            <p className="text-[13px] text-amber-800 mt-0.5">{error}</p>
          </div>
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-10 text-center">
          <FileText className="size-7 text-slate-400 mx-auto" />
          <h3 className="mt-3 text-base font-semibold text-slate-900">No contracts yet</h3>
          <p className="mt-1 text-sm text-slate-500">Your master agreement will appear here once we open your account.</p>
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <div className="space-y-3">
          {items.map((c) => (
            <ContractCard
              key={c.id}
              c={c}
              role={role}
              onSign={() => setSignOpen(c)}
              onPreview={() => onPreview(c)}
              onDownload={() => onDownload(c)}
            />
          ))}
        </div>
      )}

      {/* Signing modal */}
      {signOpen && (
        <SignModal
          contract={signOpen}
          onClose={() => setSignOpen(null)}
          onSigned={async () => {
            setSignOpen(null);
            toast.success("Contract signed. A copy has been emailed to you.");
            await load();
          }}
        />
      )}

      {/* PDF preview modal */}
      {pdfOpen && <PdfPreviewModal {...pdfOpen} onClose={() => setPdfOpen(null)} />}
    </div>
  );
}

/* ── Contract row card ──────────────────────────────────────────────────── */
function ContractCard({ c, role, onSign, onPreview, onDownload }) {
  const signed = c.status === "signed";
  return (
    <div
      className={`rounded-xl border bg-white p-5 transition-all ${
        signed ? "border-slate-200" : "border-amber-300/70 bg-amber-50/30"
      }`}
      data-testid={`contract-card-${c.id}`}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <span className={`inline-grid place-items-center size-10 rounded-lg flex-shrink-0 ${
            signed ? "bg-emerald-500/10 text-emerald-600" : "bg-amber-500/10 text-amber-700"
          }`}>
            {signed ? <Lock className="size-4" strokeWidth={2.2} /> : <FileText className="size-4" strokeWidth={1.9} />}
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-[15px] font-semibold tracking-tight text-slate-900 truncate">{c.template_title}</h3>
              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-mono font-semibold tracking-[0.14em] uppercase ${
                signed ? "bg-emerald-500/10 text-emerald-700" : "bg-amber-500/10 text-amber-800"
              }`}>
                {signed ? <><CheckCircle2 className="size-2.5" /> Signed</> : <><AlertTriangle className="size-2.5" /> Awaiting signature</>}
              </span>
            </div>
            <div className="mt-1 text-[12.5px] text-slate-500 font-mono">
              {c.id} · Issued {new Date(c.issued_at).toLocaleDateString("en-GB", { day:"2-digit", month:"short", year:"numeric" })}
              {c.signed_at && <> · Signed {new Date(c.signed_at).toLocaleDateString("en-GB", { day:"2-digit", month:"short", year:"numeric" })}</>}
              {" · v"}{c.version}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {!signed && (
            <Button
              size="sm"
              onClick={onSign}
              className="bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-full shadow-sm shadow-[#0055FF]/20"
              data-testid={`contract-sign-${c.id}`}
            >
              <Pen className="size-3.5 mr-1.5" /> Sign now
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={onPreview} className="rounded-full" data-testid={`contract-preview-${c.id}`}>
            <ExternalLink className="size-3.5 mr-1.5" /> Preview
          </Button>
          {signed && (
            <Button size="sm" variant="outline" onClick={onDownload} className="rounded-full" data-testid={`contract-download-${c.id}`}>
              <Download className="size-3.5 mr-1.5" /> Download
            </Button>
          )}
        </div>
      </div>

      {signed && c.signer && (
        <div className="mt-4 pt-4 border-t border-slate-100 grid sm:grid-cols-3 gap-3 text-[12.5px]">
          <div>
            <div className="text-slate-500 text-[10.5px] uppercase tracking-[0.14em] font-mono">Signed by</div>
            <div className="text-slate-900 font-medium mt-0.5">{c.signer.name}</div>
            <div className="text-slate-500">{c.signer.title}</div>
          </div>
          <div>
            <div className="text-slate-500 text-[10.5px] uppercase tracking-[0.14em] font-mono">Email</div>
            <div className="text-slate-900 mt-0.5 truncate">{c.signer.email}</div>
          </div>
          <div>
            <div className="text-slate-500 text-[10.5px] uppercase tracking-[0.14em] font-mono">When</div>
            <div className="text-slate-900 font-mono tabular-nums mt-0.5">
              {new Date(c.signed_at).toLocaleString("en-GB", { dateStyle:"medium", timeStyle:"short" })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Sign modal (typed + drawn tabs) ────────────────────────────────────── */
function SignModal({ contract, onClose, onSigned }) {
  const [tab, setTab]     = useState("typed");  // 'typed' | 'drawn'
  const [name, setName]   = useState("");
  const [title, setTitle] = useState("Authorised signatory");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState(contract?.counterparty?.company || "");
  const [drawnUrl, setDrawnUrl] = useState("");
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = (
    agree && name.trim().length >= 2 && email.includes("@") &&
    (tab === "typed" ? name.trim().length >= 2 : drawnUrl.startsWith("data:image"))
  );

  const submit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      await api.post("/contracts/sign", {
        contract_id: contract.id,
        method: tab,
        value: tab === "typed" ? name.trim() : drawnUrl,
        signer_name:  name.trim(),
        signer_email: email.trim(),
        signer_title: title.trim() || "Authorised signatory",
        company: company.trim() || undefined,
        agree_terms: true,
      });
      onSigned();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-slate-900/40 backdrop-blur-sm animate-fade-up" data-testid="sign-modal">
      <div className="relative w-full max-w-xl rounded-2xl bg-white shadow-[0_30px_80px_rgba(15,23,42,0.25)] border border-slate-200 overflow-hidden">
        <button onClick={onClose} className="absolute top-3 right-3 p-2 rounded-full hover:bg-slate-100 z-10" aria-label="Close" data-testid="sign-modal-close">
          <X className="size-4" />
        </button>

        <div className="px-7 pt-6 pb-5 border-b border-slate-100">
          <div className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold tracking-[0.22em] uppercase text-[#0055FF]">
            <ShieldCheck className="size-3" strokeWidth={2.4} /> E-signature
          </div>
          <h2 className="mt-2 text-xl tracking-[-0.02em] font-semibold text-slate-900">Sign {contract.template_title}</h2>
          <p className="mt-1 text-[13px] text-slate-500">
            By signing below you accept the terms of this agreement on behalf of your organisation. Connexis Group Ltd is pre-signed by <b className="text-slate-700">Kieran Flannery, Director</b>.
          </p>
        </div>

        <div className="px-7 py-5 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Tabs */}
          <div className="inline-flex rounded-lg bg-slate-100 p-1">
            <button
              type="button"
              onClick={() => setTab("typed")}
              className={`inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-md transition-colors ${
                tab === "typed" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"
              }`}
              data-testid="sign-tab-typed"
            >
              <Type className="size-3.5" /> Type
            </button>
            <button
              type="button"
              onClick={() => setTab("drawn")}
              className={`inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-md transition-colors ${
                tab === "drawn" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"
              }`}
              data-testid="sign-tab-drawn"
            >
              <Pen className="size-3.5" /> Draw
            </button>
          </div>

          {tab === "typed" ? (
            <div>
              <Label className="text-[12px] font-medium text-slate-700">Type your full legal name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Sarah Johnson"
                className="mt-1"
                data-testid="sign-typed-input"
              />
              {name.trim() && (
                <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50/60 px-4 py-3">
                  <div className="text-[10.5px] uppercase tracking-[0.14em] font-mono text-slate-500">Preview</div>
                  <div className="mt-1 text-[28px] text-slate-900" style={{ fontFamily: "'Brush Script MT', 'Lucida Handwriting', cursive" }}>
                    {name.trim()}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <SignaturePad onChange={setDrawnUrl} />
          )}

          {/* Signer details */}
          <div className="grid sm:grid-cols-2 gap-3 pt-2 border-t border-slate-100">
            <div>
              <Label className="text-[12px] font-medium text-slate-700">Your name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Sarah Johnson" className="mt-1" data-testid="sign-name" />
            </div>
            <div>
              <Label className="text-[12px] font-medium text-slate-700">Title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Director" className="mt-1" data-testid="sign-title" />
            </div>
            <div>
              <Label className="text-[12px] font-medium text-slate-700">Your email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="sarah@company.com" className="mt-1" data-testid="sign-email" />
            </div>
            <div>
              <Label className="text-[12px] font-medium text-slate-700">Company</Label>
              <Input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Company Ltd" className="mt-1" data-testid="sign-company" />
            </div>
          </div>

          <label className="flex items-start gap-2.5 pt-2 cursor-pointer" data-testid="sign-agree-label">
            <input
              type="checkbox"
              checked={agree}
              onChange={(e) => setAgree(e.target.checked)}
              className="mt-1 size-4 rounded border-slate-300 text-[#0055FF] focus:ring-[#0055FF]"
              data-testid="sign-agree-checkbox"
            />
            <span className="text-[12.5px] text-slate-600 leading-relaxed">
              I confirm I am authorised to bind my organisation to this agreement. I understand my IP address, device and timestamp will be recorded as part of the audit trail.
            </span>
          </label>
        </div>

        <div className="px-7 py-4 border-t border-slate-100 bg-slate-50/40 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-full" data-testid="sign-cancel">Cancel</Button>
          <Button
            onClick={submit}
            disabled={!canSubmit || submitting}
            className="rounded-full bg-[#0055FF] hover:bg-[#0044CC] text-white shadow-sm shadow-[#0055FF]/20"
            data-testid="sign-submit"
          >
            {submitting ? <><Loader2 className="size-4 mr-1.5 animate-spin" /> Signing…</> : <><Lock className="size-4 mr-1.5" /> Sign agreement</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ── PDF preview modal ──────────────────────────────────────────────────── */
function PdfPreviewModal({ contract, url, onClose }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-up" data-testid="pdf-preview-modal">
      <div className="relative w-full max-w-4xl h-[88vh] rounded-2xl bg-white shadow-[0_30px_80px_rgba(15,23,42,0.25)] border border-slate-200 overflow-hidden flex flex-col">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[12px] font-mono text-slate-500">{contract.id}</div>
            <div className="text-[14px] font-semibold text-slate-900 truncate">{contract.template_title}</div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <a
              href={url + (url.includes("?") ? "&" : "?") + "dl=1"}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-[12px] font-medium text-slate-700 hover:text-[#0055FF] px-3 h-9 rounded-full border border-slate-200 hover:border-[#0055FF]/40 transition-colors"
            >
              <Download className="size-3.5" /> Download
            </a>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100" aria-label="Close" data-testid="pdf-preview-close">
              <X className="size-4" />
            </button>
          </div>
        </div>
        <iframe src={url} className="flex-1 w-full bg-slate-50" title="Contract preview" />
      </div>
    </div>
  );
}
